import type {
  IDesignStoreFormShape,
  IStoreInfoFormShape,
} from '../StoreDetails/storeDetails.types';
import type { ISizesFormValues } from '../StoreConfirmSizes/helpers/storeConfirmSizes.types';
import type { DesignGroup } from '../types/storeReviewOrderStep.types';
import { isDefaultFreshPrintStoreLogo } from './storeCheckoutUiVariationBranding.utils';
import {
  serializeEditableDesignStore,
  serializeEditableSizes,
} from './storeCheckoutEditChanges.utils';
import { hasAtLeastOneValidItemDonation } from './storeCheckoutLaunchValidation.utils';

export interface INewStoreCheckoutSetupProgressInput {
  storeType: string;
  defaultStoreType: string;
  storeInfo: IStoreInfoFormShape;
  defaultStoreInfo: IStoreInfoFormShape;
  designStore: IDesignStoreFormShape;
  defaultDesignStore: IDesignStoreFormShape;
  sizes: ISizesFormValues;
  defaultSizes: ISizesFormValues;
  designGroups: DesignGroup[];
  donationAmounts: Record<string, string>;
  paymentOption: string;
  defaultPaymentOption: string;
  shippingOption: string;
  defaultShippingOption: string;
  shipTypeKey: string;
  defaultShipTypeKey: string;
  orderName: string;
  defaultOrderName: string;
  hasCachedOrderIdInUrl: boolean;
  isOnPaymentStep: boolean;
}

const normalizeDesignStoreForSetupProgress = (
  designStore: IDesignStoreFormShape
): IDesignStoreFormShape => {
  const usesDefaultBranding = isDefaultFreshPrintStoreLogo(designStore.storeLogo);

  return {
    ...designStore,
    storeLogo: usesDefaultBranding ? '' : designStore.storeLogo,
    selectedColors: [...designStore.selectedColors],
  };
};

const hasStoreInfoProgress = (
  storeInfo: IStoreInfoFormShape,
  defaultStoreInfo: IStoreInfoFormShape
): boolean => {
  if (storeInfo.displayMadeWith !== defaultStoreInfo.displayMadeWith) {
    return true;
  }

  const textFields: Array<
    keyof Pick<
      IStoreInfoFormShape,
      'storeName' | 'orderUntil' | 'storeDescription' | 'charityName' | 'raiseAmount'
    >
  > = ['storeName', 'orderUntil', 'storeDescription', 'charityName', 'raiseAmount'];

  return textFields.some(
    (field) => (storeInfo[field] ?? '').trim() !== (defaultStoreInfo[field] ?? '').trim()
  );
};

const hasSizeSelectionProgress = (sizes: ISizesFormValues): boolean =>
  sizes.sizes.some((group) => Object.values(group.sizesSelected).some((size) => size.selected));

const hasDesignGroupProgress = (designGroups: DesignGroup[]): boolean =>
  designGroups.some((group) => group.pricingModel != null);

export const hasNewStoreCheckoutSetupProgress = (
  input: INewStoreCheckoutSetupProgressInput
): boolean => {
  if (input.isOnPaymentStep || input.hasCachedOrderIdInUrl) {
    return true;
  }

  if (input.storeType !== input.defaultStoreType) {
    return true;
  }

  if (hasStoreInfoProgress(input.storeInfo, input.defaultStoreInfo)) {
    return true;
  }

  const normalizedCurrentDesign = normalizeDesignStoreForSetupProgress(input.designStore);
  const normalizedDefaultDesign = normalizeDesignStoreForSetupProgress(input.defaultDesignStore);

  if (
    serializeEditableDesignStore(normalizedCurrentDesign) !==
    serializeEditableDesignStore(normalizedDefaultDesign)
  ) {
    return true;
  }

  if (
    serializeEditableSizes(input.sizes) !== serializeEditableSizes(input.defaultSizes) &&
    hasSizeSelectionProgress(input.sizes)
  ) {
    return true;
  }

  if (hasDesignGroupProgress(input.designGroups)) {
    return true;
  }

  if (
    input.storeType === 'fundraiser' &&
    hasAtLeastOneValidItemDonation(input.designGroups, input.donationAmounts)
  ) {
    return true;
  }

  if (input.paymentOption.trim() !== input.defaultPaymentOption.trim()) {
    return true;
  }

  if (input.shippingOption.trim() !== input.defaultShippingOption.trim()) {
    return true;
  }

  if (input.shipTypeKey.trim() !== input.defaultShipTypeKey.trim()) {
    return true;
  }

  if (input.orderName.trim() !== input.defaultOrderName.trim()) {
    return true;
  }

  return false;
};
